import {useEffect, useMemo} from "react";
import * as Sentry from "@sentry/react";
import {
    Artifact,
    DirectSearchResult,
    LocalStorageKeys,
} from "../../../../types";
import {bffApi} from "../../bffApi";
import {SearchResultsRenderer} from "./SearchResultsRenderer";

/**
 * Wraps SearchResultsRenderer for the message-attached (tool-suggestion) path and emits
 * impression/click-through metrics. Kept separate from SearchResultsRenderer because that
 * component is also used in non-tool-suggestion contexts (PostSearchPanel) that must not
 * be counted.
 *
 * "Shown" fires once per artifact per page load, and only for artifacts that arrived on
 * a live stream (latencyMs is stamped only there) — rehydrated artifacts from thread
 * history never re-emit, so reopening a thread does not inflate the funnel.
 */
const trackedImpressions = new Set<string>();

/** Stable identity for a suggested tool across searches and sessions. */
function toolKey(result: DirectSearchResult): string {
    return result.metadata?.topicId ?? result.url;
}

/**
 * First open of a tool from a suggestion counts as "clicked"; any later open of the
 * same tool counts as "reused". Persisted per user per browser (same scoping pattern
 * as feature flags); the backend also receives the tool key as SubjectId so the
 * funnel can be derived authoritatively from event history on the analytics side.
 */
function seenStorageKey(brukerKey: string | undefined): string {
    return `${LocalStorageKeys.ClickedToolSuggestions}:${brukerKey ?? ""}`;
}

function readSeenTools(storageKey: string): string[] {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
    } catch {
        // fall through to self-heal
    }
    // Corrupt value: remove it so the classifier can recover instead of
    // classifying every future click as "clicked" forever.
    try {
        localStorage.removeItem(storageKey);
    } catch {
        // storage unavailable
    }
    return [];
}

function markToolSeen(storageKey: string, key: string): void {
    try {
        localStorage.setItem(
            storageKey,
            JSON.stringify([...readSeenTools(storageKey), key])
        );
    } catch {
        // storage unavailable — the next click counts as "clicked" again
    }
}

export function TrackedSearchResults({
    artifact,
    messageContent,
}: {
    artifact: Artifact;
    messageContent?: string;
}) {
    const results = useMemo(
        () => (artifact.payload?.results as DirectSearchResult[]) ?? [],
        [artifact.payload?.results]
    );
    const [trackToolSuggestion] =
        bffApi.endpoints.trackToolSuggestion.useMutation();
    const user = bffApi.endpoints.getUser.useQuery();

    const liveArrival = artifact.latencyMs !== undefined;

    useEffect(() => {
        if (
            !liveArrival ||
            trackedImpressions.has(artifact.id) ||
            results.length === 0
        )
            return;
        trackedImpressions.add(artifact.id);
        Sentry.metrics.count("chat.tool_suggestion_shown", 1, {
            attributes: {resultCount: results.length},
        });
        trackToolSuggestion({
            action: "shown",
            value: results.length,
            durationMs: artifact.latencyMs,
            toolIds: results.map(toolKey),
        });
    }, [
        liveArrival,
        artifact.id,
        artifact.latencyMs,
        results,
        trackToolSuggestion,
    ]);

    return (
        <SearchResultsRenderer
            artifact={artifact}
            messageContent={messageContent}
            onResultClick={(result, index) => {
                // Only live tool suggestions count: rehydrated thread history and
                // client-constructed search artifacts (splash search, thread seeds)
                // must not produce clicks without a matching "shown".
                if (!liveArrival) return;
                Sentry.metrics.count("chat.tool_suggestion_clicked", 1, {
                    attributes: {
                        topicType: result.metadata?.topicType ?? "unknown",
                        position: index,
                    },
                });
                const key = toolKey(result);
                const storageKey = seenStorageKey(user.data?.brukerKey);
                const action = readSeenTools(storageKey).includes(key)
                    ? "reused"
                    : "clicked";
                trackToolSuggestion({action, toolKey: key})
                    .unwrap()
                    .then(() => {
                        // Persist only after the event landed, so a lost first
                        // click cannot turn later clicks into false "reused".
                        if (action === "clicked") markToolSeen(storageKey, key);
                    })
                    .catch(() => {
                        // Tracking failure never affects the user.
                    });
            }}
        />
    );
}
